/**
 * Campaign player flow: tree screen → ink transition → goal reveal → mode run → claim / retry.
 */

import { showChallengeRevealText } from "../challenges/roulette-ui";
import { showCampaignRewardReveal } from "./reward-reveal";
import {
  isPlayTransitionEnabled,
  runPlayTransition,
  type PlayTransitionOrigin,
} from "../play-transition";
import {
  fetchCampaignTree,
  postCampaignAbandon,
  postCampaignClaim,
  postCampaignStart,
} from "./api";
import { mountCampaignTree, type CampaignTreeApi } from "./tree-ui";
import {
  campaignModeIsRace,
  formatCampaignGoalReveal,
  type CampaignAttempt,
  type CampaignNodeView,
} from "./types";

export type CampaignRuntimeHooks = {
  isSignedIn: () => boolean;
  openSignIn: () => void;
  showCampaignScreen: () => void;
  hideCampaignScreen: () => void;
  getCampaignMount: () => HTMLElement | null;
  startModeRun: (
    modeId: string,
    opts?: { levelIndex?: number | null; layoutRef?: string | null },
  ) => void | Promise<void>;
  endRunQuiet: () => void;
  /** Exit pointer lock / hideCursor and return chrome to menu after a node ends. */
  restoreMenuCursor: () => void;
  /** Hide Game Over UI and clear death so Play Again can start a fresh run. */
  prepCampaignRetry: () => void;
  /** Hide Game Over when leaving back to the Campaign tree. */
  hideCampaignGameOver: () => void;
  returnToHub: () => void;
  getScore: () => number;
  getCampaignProgress: (modeId: string, metric: "score" | "time") => number;
  setDotsBalance: (balance: number) => void;
  addOwnedCosmetic: (id: string) => void;
  setReturnBtnLabel: (label: string) => void;
  setFadeOverlay: (opacity: number) => void;
  setCampaignRunPaused: (paused: boolean) => void;
  playModeCardBeep: () => void;
  /** Same edge pulse as Challenges when the node goal is met mid-run. */
  setCampaignGoalCompleteFx: (active: boolean) => void;
  playSfx: (kind: "goal_complete" | "reveal_show" | "reveal_hide" | "reward_claim") => void;
  /** Persist personal best / rankings for the active mode (same as a normal run). */
  persistRunBestIfAny: () => void;
};

type Phase = "idle" | "tree" | "entering" | "playing" | "gameover" | "claiming" | "done";

const FOCUS_NODE_KEY = "cursorGameCampaignFocusNode";

let hooks: CampaignRuntimeHooks | null = null;
let phase: Phase = "idle";
let treeApi: CampaignTreeApi | null = null;
let nodes: CampaignNodeView[] = [];
let attempt: CampaignAttempt | null = null;
let busy = false;
let playElapsedAccum = 0;
let lastPlayTick = 0;
let goalCheckReadyAt = 0;
let progressBaseline = 0;
let claimBackoffUntil = 0;
let returnArmed = false;
let returnArmUntil = 0;
/** Goal reached this attempt — keep playing; claim waits until death (or Return). */
let goalCompleted = false;

function readFocusNodeId(): string | null {
  try {
    const v = localStorage.getItem(FOCUS_NODE_KEY);
    return typeof v === "string" && v.trim() ? v.trim() : null;
  } catch {
    return null;
  }
}

function writeFocusNodeId(id: string) {
  if (!id) return;
  try {
    localStorage.setItem(FOCUS_NODE_KEY, id);
  } catch {
    /* ignore quota / private mode */
  }
}

/** Prefer the last node the player touched; otherwise the best “current progress” node. */
function resolveFocusNodeId(list: CampaignNodeView[]): string | null {
  const preferred = readFocusNodeId();
  if (preferred && list.some((n) => n.id === preferred)) return preferred;

  const available = list
    .filter((n) => n.state === "available")
    .sort((a, b) => a.sortOrder - b.sortOrder || a.posY - b.posY || a.posX - b.posX);
  if (available.length) return available[0].id;

  const completed = list
    .filter((n) => n.state === "completed")
    .sort((a, b) => b.sortOrder - a.sortOrder || b.posY - a.posY || b.posX - a.posX);
  if (completed.length) return completed[0].id;

  return list[0]?.id ?? null;
}

export function mountCampaignRuntime(h: CampaignRuntimeHooks): void {
  hooks = h;
}

export function isCampaignActive(): boolean {
  return phase !== "idle";
}

export function isCampaignTreeVisible(): boolean {
  return phase === "tree";
}

export function isCampaignAttemptPlaying(): boolean {
  return phase === "playing";
}

export function isCampaignGoalCompleted(): boolean {
  return goalCompleted && phase === "playing";
}

export function getCampaignAttempt(): CampaignAttempt | null {
  return attempt;
}

/** SpeedRun / Golf Campaign: seconds left before Time's up (null if not a race run). */
export function getCampaignRaceRemainingSec(): number | null {
  if (phase !== "playing" || !attempt || !campaignModeIsRace(attempt.modeId)) return null;
  return Math.max(0, Math.max(1, attempt.goalValue) - playElapsedAccum);
}

export function isCampaignRaceTimedOut(): boolean {
  if (phase !== "playing" || !attempt || !campaignModeIsRace(attempt.modeId) || goalCompleted) {
    return false;
  }
  if (performance.now() < goalCheckReadyAt) return false;
  return playElapsedAccum >= attempt.goalValue;
}

export function isCampaignGameOver(): boolean {
  return phase === "gameover";
}

export function isCampaignClaiming(): boolean {
  return phase === "claiming" || phase === "entering";
}

export function campaignPauseBlocked(): boolean {
  return isCampaignActive() && phase !== "playing";
}

function clearCampaignGoalCompleteFx() {
  goalCompleted = false;
  hooks?.setCampaignGoalCompleteFx(false);
}

/** Mark the Campaign node goal as met without claiming yet — keep playing. */
export function markCampaignGoalCompleted(): void {
  if (!hooks || phase !== "playing" || !attempt || goalCompleted) return;
  goalCompleted = true;
  hooks.playSfx("goal_complete");
  hooks.setCampaignGoalCompleteFx(true);
}

function clearTree() {
  treeApi?.destroy();
  treeApi = null;
}

async function refreshTree() {
  if (!hooks) return;
  const mount = hooks.getCampaignMount();
  if (!mount) return;
  const data = await fetchCampaignTree();
  if (!data) {
    mount.innerHTML = "";
    const hint = document.createElement("p");
    hint.className = "accessHint";
    hint.textContent = "Sign in to play Campaign.";
    mount.appendChild(hint);
    return;
  }
  nodes = data.nodes || [];
  if (!nodes.length) {
    mount.innerHTML = "";
    const hint = document.createElement("p");
    hint.className = "accessHint";
    hint.textContent = "No Campaign nodes yet. Check back after an admin publishes the tree.";
    mount.appendChild(hint);
    treeApi = null;
    return;
  }
  treeApi?.destroy();
  const focusNodeId = resolveFocusNodeId(nodes);
  treeApi = mountCampaignTree({
    root: mount,
    nodes,
    interactive: true,
    focusNodeId,
    onNodeActivate: (node, el) => {
      writeFocusNodeId(node.id);
      void beginNodeRun(node, {
        x: el.getBoundingClientRect().left + el.offsetWidth / 2,
        y: el.getBoundingClientRect().top + el.offsetHeight / 2,
      });
    },
  });
}

export async function openCampaignFlow(): Promise<void> {
  if (!hooks) return;
  if (!hooks.isSignedIn()) {
    hooks.openSignIn();
    return;
  }
  phase = "tree";
  busy = false;
  attempt = null;
  returnArmed = false;
  hooks.setReturnBtnLabel("Return");
  hooks.showCampaignScreen();
  hooks.playModeCardBeep();
  try {
    await refreshTree();
  } catch {
    const mount = hooks.getCampaignMount();
    if (mount) {
      mount.innerHTML = "";
      const hint = document.createElement("p");
      hint.className = "accessHint";
      hint.textContent = "Could not load Campaign. Try again later.";
      mount.appendChild(hint);
    }
  }
}

export function closeCampaignFlow(): void {
  void postCampaignAbandon();
  clearTree();
  clearCampaignGoalCompleteFx();
  phase = "idle";
  attempt = null;
  busy = false;
  hooks?.hideCampaignScreen();
}

export function leaveCampaignToHub(): void {
  closeCampaignFlow();
  hooks?.returnToHub();
}

export function handleCampaignReturn(): boolean {
  if (!isCampaignActive()) return false;
  const now = performance.now();
  if (phase === "gameover") {
    void leaveCampaignFromGameOver();
    return true;
  }
  if (phase === "playing") {
    if (goalCompleted && attempt) {
      void claimCampaignAndPresent();
      return true;
    }
    if (!returnArmed || now > returnArmUntil) {
      returnArmed = true;
      returnArmUntil = now + 2500;
      hooks?.setReturnBtnLabel("Sure?");
      return true;
    }
    returnArmed = false;
    hooks?.setReturnBtnLabel("Return");
    void failNodeAttempt();
    return true;
  }
  if (phase === "tree" || phase === "done") {
    leaveCampaignToHub();
    return true;
  }
  return true;
}

export function noteCampaignPlayFrame(now: number, paused: boolean): void {
  if (phase !== "playing") return;
  if (paused) {
    lastPlayTick = now;
    return;
  }
  if (lastPlayTick > 0) playElapsedAccum += (now - lastPlayTick) / 1000;
  lastPlayTick = now;
}

export function checkCampaignGoalMet(): boolean {
  if (phase !== "playing" || !attempt || !hooks) return false;
  if (performance.now() < goalCheckReadyAt) return false;
  if (performance.now() < claimBackoffUntil) return false;
  // Race modes complete only by finishing the level (beginSpeedrunWin / beginPenaltyWin).
  if (campaignModeIsRace(attempt.modeId)) return false;
  if (attempt.metric === "time") {
    return playElapsedAccum >= attempt.goalValue;
  }
  const progress = hooks.getCampaignProgress(attempt.modeId, attempt.metric);
  if (progress < attempt.goalValue) return false;
  // If baseline was sampled before the mode reset score (stale prior run), it can be
  // >= goal and permanently block clears. Treat that as already past the floor.
  if (progressBaseline >= attempt.goalValue) return true;
  return progress > progressBaseline;
}

function waitMs(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function beginNodeRun(node: CampaignNodeView, origin: PlayTransitionOrigin): Promise<void> {
  if (!hooks || busy || phase !== "tree") return;
  if (node.state === "locked") return;
  busy = true;
  phase = "entering";
  hooks.playModeCardBeep();

  let started: Awaited<ReturnType<typeof postCampaignStart>>;
  try {
    started = await postCampaignStart(node.id);
  } catch (e) {
    console.warn("[campaign] start failed", e);
    phase = "tree";
    busy = false;
    return;
  }
  attempt = started.attempt;
  writeFocusNodeId(node.id);

  const coverAndReveal = async () => {
    if (!hooks || !attempt) return;
    hooks.hideCampaignScreen();
    clearTree();
    hooks.setFadeOverlay(1);
    const lines = formatCampaignGoalReveal(
      attempt.modeLabel,
      attempt.metric,
      attempt.goalValue,
      attempt.modeId,
      attempt.levelIndex,
    );
    await showChallengeRevealText(lines, 1800);
    hooks.setFadeOverlay(0);
    playElapsedAccum = 0;
    lastPlayTick = 0;
    phase = "playing";
    busy = false;
    hooks.setReturnBtnLabel("Return");
    returnArmed = false;
    // Start the mode first so score/level reset, then sample the baseline.
    // Sampling before startModeRun reused a prior run's score and could make
    // `progress > progressBaseline` impossible for the Campaign goal.
    await Promise.resolve(
      hooks.startModeRun(attempt.modeId, {
        levelIndex: attempt.levelIndex,
        layoutRef: attempt.layoutRef,
      }),
    );
    progressBaseline = hooks.getCampaignProgress(attempt.modeId, attempt.metric);
    goalCheckReadyAt = performance.now() + 900;
    claimBackoffUntil = 0;
    clearCampaignGoalCompleteFx();
  };

  if (isPlayTransitionEnabled()) {
    // Ink blot (CursorGame clipped inside) → full black, then Challenges-style goal text.
    runPlayTransition(
      () => {
        hooks?.hideCampaignScreen();
        clearTree();
        hooks?.setFadeOverlay(1);
      },
      { origin, armPointerLock: true },
    );
    await waitMs(760);
    await coverAndReveal();
  } else {
    await coverAndReveal();
  }
}

async function failNodeAttempt(): Promise<void> {
  if (!hooks) return;
  clearCampaignGoalCompleteFx();
  await postCampaignAbandon();
  attempt = null;
  hooks.setCampaignRunPaused(false);
  hooks.endRunQuiet();
  hooks.restoreMenuCursor();
  hooks.setFadeOverlay(0);
  phase = "tree";
  busy = false;
  hooks.setReturnBtnLabel("Return");
  returnArmed = false;
  hooks.showCampaignScreen();
  await refreshTree().catch(() => {});
}

async function leaveCampaignFromGameOver(): Promise<void> {
  if (!hooks || phase !== "gameover") return;
  busy = true;
  hooks.hideCampaignGameOver();
  await failNodeAttempt();
}

/** Mark Campaign as on the normal Game Over screen (Play Again / Return). */
export function noteCampaignGameOverShown(): void {
  if (phase !== "playing") return;
  phase = "gameover";
  busy = false;
  hooks?.setReturnBtnLabel("Return");
  returnArmed = false;
}

/** Play Again from Campaign Game Over — fresh attempt of the same node. */
export async function retryCampaignFromGameOver(): Promise<void> {
  if (!hooks || phase !== "gameover" || !attempt || busy) return;
  busy = true;
  const nodeId = attempt.nodeId;
  let started: Awaited<ReturnType<typeof postCampaignStart>>;
  try {
    started = await postCampaignStart(nodeId);
  } catch (e) {
    console.warn("[campaign] retry start failed", e);
    busy = false;
    return;
  }
  attempt = started.attempt;
  writeFocusNodeId(nodeId);
  hooks.prepCampaignRetry();
  playElapsedAccum = 0;
  lastPlayTick = 0;
  phase = "playing";
  busy = false;
  hooks.setReturnBtnLabel("Return");
  returnArmed = false;
  await Promise.resolve(
    hooks.startModeRun(attempt.modeId, {
      levelIndex: attempt.levelIndex,
      layoutRef: attempt.layoutRef,
    }),
  );
  progressBaseline = hooks.getCampaignProgress(attempt.modeId, attempt.metric);
  goalCheckReadyAt = performance.now() + 900;
  claimBackoffUntil = 0;
  clearCampaignGoalCompleteFx();
}

/** Goal met mid-run: keep playing with edge FX. CLEARED waits until death/Return. */
export function onCampaignGoalReached(): void {
  markCampaignGoalCompleted();
}

/**
 * Death (or Return) after the node goal was already met — claim reward + CLEARED.
 * Mirrors Challenges' claim-after-goal flow.
 */
export async function onCampaignDeathWithGoal(): Promise<void> {
  await claimCampaignAndPresent();
}

async function claimCampaignAndPresent(): Promise<void> {
  if (!hooks || !attempt || busy) return;
  if (phase !== "playing" || !goalCompleted) return;

  busy = true;
  phase = "claiming";
  hooks.setReturnBtnLabel("Return");
  returnArmed = false;
  hooks.persistRunBestIfAny();
  clearCampaignGoalCompleteFx();

  const token = attempt.token;
  const modeId = attempt.modeId;
  const metric = attempt.metric;
  const goalValue = attempt.goalValue;
  let score = hooks.getCampaignProgress(modeId, metric);
  let timeSec = playElapsedAccum;
  if (campaignModeIsRace(modeId)) {
    // Server accepts race clears with score >= 1 (level finished).
    score = Math.max(score, 1);
  } else if (metric === "score") {
    score = Math.max(score, goalValue);
  } else {
    timeSec = Math.max(timeSec, goalValue);
  }

  hooks.setCampaignRunPaused(true);
  hooks.setFadeOverlay(1);

  try {
    const claim = await postCampaignClaim(token, { score, timeSec });
    if (typeof claim.dotsBalance === "number") hooks.setDotsBalance(claim.dotsBalance);
    if (claim.rewardCosmeticId) hooks.addOwnedCosmetic(claim.rewardCosmeticId);

    const lines: string[] = ["CLEARED"];
    if (claim.rewardGranted) {
      if (claim.rewardDots > 0) lines.push(`+${claim.rewardDots} Dots`);
      if (claim.rewardCosmeticName) lines.push(`+ ${claim.rewardCosmeticName}`);
    } else if (claim.alreadyClaimed) {
      lines.push("Already cleared");
    }
    hooks.playSfx("reveal_show");
    const preview =
      claim.rewardGranted && claim.rewardCosmeticImageData
        ? {
            name: claim.rewardCosmeticName,
            imageData: claim.rewardCosmeticImageData,
            animationFrames: claim.rewardCosmeticAnimationFrames,
            newCursor: !!claim.rewardCosmeticNewCursor,
          }
        : null;
    if (preview || (claim.rewardGranted && claim.rewardDots > 0)) {
      await showCampaignRewardReveal({
        lines,
        cosmetic: preview,
        holdMs: preview ? 2800 : 2000,
      });
    } else {
      await showChallengeRevealText(lines, 1800);
    }
    hooks.playSfx("reward_claim");
    hooks.playSfx("reveal_hide");
  } catch (e) {
    const err = e as { status?: number; data?: Record<string, unknown> };
    const data = (err?.data || {}) as Record<string, unknown>;
    console.warn("[campaign] claim failed", e);

    // Server may have already completed the node (retry / race) — still show CLEARED.
    if (data.ok === true || data.alreadyClaimed === true || Array.isArray(data.completedNodeIds)) {
      if (typeof data.dotsBalance === "number") hooks.setDotsBalance(data.dotsBalance as number);
      if (typeof data.rewardCosmeticId === "string") {
        hooks.addOwnedCosmetic(data.rewardCosmeticId);
      }
      const lines: string[] = ["CLEARED"];
      if (data.alreadyClaimed) lines.push("Already cleared");
      hooks.playSfx("reveal_show");
      await showChallengeRevealText(lines, 1800);
      hooks.playSfx("reveal_hide");
    } else {
      // Hard failure after death: do not resume the dead run — back to tree.
      // Keep the attempt abandoned so the player isn't stuck mid-claim.
      claimBackoffUntil = performance.now() + 1500;
      try {
        await postCampaignAbandon();
      } catch {
        /* ignore */
      }
      attempt = null;
      hooks.setCampaignRunPaused(false);
      hooks.endRunQuiet();
      hooks.restoreMenuCursor();
      hooks.setFadeOverlay(0);
      phase = "tree";
      busy = false;
      hooks.showCampaignScreen();
      await refreshTree().catch(() => {});
      return;
    }
  }

  attempt = null;
  hooks.setCampaignRunPaused(false);
  hooks.endRunQuiet();
  hooks.restoreMenuCursor();
  hooks.setFadeOverlay(0);
  phase = "tree";
  busy = false;
  hooks.showCampaignScreen();
  await refreshTree().catch(() => {});
}
